import React from "react";
import { BsFileText } from "react-icons/bs";
import { defineMessages, FormattedMessage } from "react-intl";

import { Attachment } from "lib/build/attachments";

import { AttachmentTable } from "src/common/components/AttachmentTable";
import {
	Card,
	CardContent,
	CardFooter,
	CardHeader,
	CardMessage,
	CardTitle
} from "src/common/components/Card";
import { Popover } from "src/common/components/Popover";
import { FullCardPreloader } from "src/common/components/Preloader";
import { Timeline, TimelineItemValue } from "src/common/components/Timeline";
import { Permission, useHasPermission } from "src/features/auth";
import { PatientLink, usePatientIdParam } from "src/features/patient";
import { node as patientConditionForm } from "src/modules/ltx/forms/patientCondition";
import {
	PatientConditionReportValue,
	PatientConditionValue
} from "src/modules/ltx/status-history/types";
import { usePatientConditionEvents } from "src/modules/ltx/status-history/usePatientConditionEvents";
import { usePatientConditionTimelineItems } from "src/modules/ltx/status-history/usePatientConditionTimelineItems";

const messages = defineMessages({
	title: {
		id: "status_history.title",
		defaultMessage: "Állapotváltozás áttekintő"
	},
	status: {
		id: "status_history.status",
		defaultMessage: "Státusz"
	},
	noChanges: {
		id: "status_history.no_changes",
		defaultMessage: "Nincsenek állapotváltozások"
	},
	newChange: {
		id: "status_history.new_change",
		defaultMessage: "Új állapotváltozás"
	}
});

export const StatusHistory: React.FunctionComponent = () => {
	const patientId = usePatientIdParam();
	const hasLimitedAccessPermission = useHasPermission(
		Permission.LimitedAccessFormRW
	);
	const { isLoading, events } = usePatientConditionEvents(patientId);
	const items = usePatientConditionTimelineItems(events);
	return (
		<Card>
			<CardHeader>
				<CardTitle>
					<FormattedMessage {...messages.title} />
				</CardTitle>
			</CardHeader>
			<CardContent>
				{isLoading ? (
					<FullCardPreloader />
				) : items.length === 0 ? (
					<CardMessage>
						<FormattedMessage {...messages.noChanges} />
					</CardMessage>
				) : (
					<Timeline<{
						attachments: Attachment[];
						data:
							| PatientConditionReportValue
							| PatientConditionValue;
					}>
						items={items}
						renderActions={renderActions}
						renderContent={renderContent}
					/>
				)}
			</CardContent>
			<CardFooter>
				{hasLimitedAccessPermission && (
					<PatientLink
						to={`/form/${patientConditionForm.id}`}
						className="button button--primary"
					>
						<FormattedMessage {...messages.newChange} />
					</PatientLink>
				)}
			</CardFooter>
		</Card>
	);
};

function renderActions({
	attachments
}: {
	attachments: Attachment[];
}): React.ReactNode {
	if (attachments.length === 0) {
		return null;
	}
	return (
		<Popover
			target={<BsFileText />}
			content={<AttachmentTable attachments={attachments} />}
		/>
	);
}

function renderContent({
	data
}: {
	data: PatientConditionReportValue | PatientConditionValue;
}): React.ReactNode {
	return <TimelineItemValue value={data.description} />;
}
